import { Action, Reducer } from 'redux'
import { createSelector, OutputSelector } from 'reselect'
import { formatLocation } from 'apps/marketplace/utils/helpers/formatShipperLaneRow'
import { fromShipperRateResponse } from 'apps/marketplace/utils/helpers/shipperRates'
import { IShipperLane } from '../shipperLanes/types'
import { IMarketplaceStore } from '../types'
import {
  GET_SHIPPER_LANE_ACTIONS as g,
  CREATE_SHIPPER_RATE_ACTIONS as c,
  UPDATE_SHIPPER_RATE_ACTIONS as u,
  CLEAR_SHIPPER_LANE,
  ADD_SHIPPER_RATE_MODAL,
} from './actions'
import { IShipperLaneState, IShipperRate, IShipperRateResponse } from './types'

interface IRequestAction extends Action<string> {
  payload: {
    requestData: {}
  }
}

interface ISuccessAction extends Action<string> {
  payload: {
    shipperLane: IShipperLane
    shipperRates: IShipperRate[]
    totalCount: number
  }
}

interface IErrorAction extends Action<string> {
  error: string
}

interface IRateSuccessAction extends Action<string> {
  payload: IShipperRateResponse
}

interface IRateModalAction extends Action<string> {
  payload?: {
    shipperRate?: IShipperRate
  }
}

type ShipperLaneAction = IRequestAction | ISuccessAction | IRateSuccessAction | IErrorAction

const defaultState: IShipperLaneState = {
  fetching: false,
  modalOpen: false,
  modalSaving: false,
  modalError: '',
  totalCount: 0,
  shipperRates: [],
  currentShipperRate: undefined,
}

/** Reducer */
const shipperLaneReducer: Reducer<IShipperLaneState, ShipperLaneAction> = (
  state = defaultState,
  action
) => {
  switch (action.type) {
    case g.GET_SHIPPER_LANE_REQUEST: {
      return {
        ...state,
        fetching: true,
        error: undefined,
      }
    }
    case g.GET_SHIPPER_LANE_SUCCESS: {
      const {
        payload: { shipperLane, shipperRates, totalCount },
      } = action as ISuccessAction
      return {
        ...state,
        fetching: false,
        shipperLane,
        shipperRates,
        totalCount,
      }
    }
    case g.GET_SHIPPER_LANE_FAILURE: {
      return {
        ...state,
        fetching: false,
        error: (action as IErrorAction).error,
      }
    }
    case c.CREATE_SHIPPER_RATE_REQUEST: {
      return {
        ...state,
        modalSaving: true,
      }
    }
    case c.CREATE_SHIPPER_RATE_FAILURE: {
      return {
        ...state,
        modalSaving: false,
        modalError: (action as IErrorAction).error,
      }
    }
    case c.CREATE_SHIPPER_RATE_SUCCESS: {
      const response = (action as IRateSuccessAction).payload
      const shipperRate = fromShipperRateResponse(response)
      return {
        ...state,
        modalSaving: false,
        modalOpen: false,
        shipperRates: [...state.shipperRates, shipperRate],
      }
    }
    case u.UPDATE_SHIPPER_RATE_REQUEST: {
      return {
        ...state,
        modalSaving: true,
      }
    }
    case u.UPDATE_SHIPPER_RATE_FAILURE: {
      return {
        ...state,
        modalSaving: false,
        modalError: (action as IErrorAction).error,
      }
    }
    case u.UPDATE_SHIPPER_RATE_SUCCESS: {
      const response = (action as IRateSuccessAction).payload
      const shipperRate = fromShipperRateResponse(response)
      const shipperRates = state.shipperRates.map(rate =>
        rate.id === shipperRate.id ? shipperRate : rate
      )
      return {
        ...state,
        modalSaving: false,
        modalOpen: false,
        shipperRates,
      }
    }
    case ADD_SHIPPER_RATE_MODAL.ADD_SHIPPER_RATE_MODAL_OPEN: {
      return {
        ...state,
        modalOpen: true,
        currentShipperRate: (action as IRateModalAction).payload?.shipperRate,
      }
    }
    case ADD_SHIPPER_RATE_MODAL.ADD_SHIPPER_RATE_MODAL_CLOSE: {
      return {
        ...state,
        modalOpen: false,
      }
    }
    case CLEAR_SHIPPER_LANE: {
      return {
        ...state,
        shipperLane: undefined,
        shipperRates: [],
      }
    }
    default:
      return state
  }
}
export default shipperLaneReducer

/** Selectors */
export const selectShipperLane = (state: IMarketplaceStore): IShipperLane | undefined =>
  state.shipperLane.shipperLane
export const selectShipperRates = (state: IMarketplaceStore): IShipperRate[] | undefined =>
  state.shipperLane.shipperRates

export const selectModalOpen = (state: IMarketplaceStore): boolean => state.shipperLane.modalOpen

export const selectModalSaving = (state: IMarketplaceStore): boolean =>
  state.shipperLane.modalSaving

export const selectModalError = (state: IMarketplaceStore): string => state.shipperLane.modalError

export const selectCurrentShipperRate = (state: IMarketplaceStore): IShipperRate | undefined =>
  state.shipperLane.currentShipperRate

interface SelectShipperLanePickupCityState
  extends OutputSelector<
    IMarketplaceStore,
    {
      pickupCityState: string
    },
    (
      res: IShipperLane | undefined
    ) => {
      pickupCityState: string
    }
  > {}

interface SelectShipperLaneOrigin
  extends OutputSelector<
    IMarketplaceStore,
    {
      origin: string
    },
    (
      res: IShipperLane | undefined
    ) => {
      origin: string
    }
  > {}

interface SelectShipperLaneDeliveryCityState
  extends OutputSelector<
    IMarketplaceStore,
    {
      deliveryCityState: string
    },
    (
      res: IShipperLane | undefined
    ) => {
      deliveryCityState: string
    }
  > {}

interface SelectShipperLaneDestination
  extends OutputSelector<
    IMarketplaceStore,
    {
      destination: string
    },
    (
      res: IShipperLane | undefined
    ) => {
      destination: string
    }
  > {}

export const shipperLanePickupCityStateSelector: SelectShipperLanePickupCityState = createSelector(
  selectShipperLane,
  shipperLane => ({
    pickupCityState: formatLocation(shipperLane?.pickupCity, shipperLane?.pickupStateCode),
  })
)
export const shipperLaneOriginSelector: SelectShipperLaneOrigin = createSelector(
  selectShipperLane,
  shipperLane => ({
    origin: formatLocation(
      shipperLane?.pickupCity,
      shipperLane?.pickupStateCode,
      shipperLane?.pickupPostalCode,
      shipperLane?.pickupPostalCode3
    ),
  })
)

export const shipperLaneDeliveryCityStateSelector: SelectShipperLaneDeliveryCityState = createSelector(
  selectShipperLane,
  shipperLane => ({
    deliveryCityState: formatLocation(shipperLane?.deliveryCity, shipperLane?.deliveryStateCode),
  })
)

export const shipperLaneDestinationSelector: SelectShipperLaneDestination = createSelector(
  selectShipperLane,
  shipperLane => ({
    destination: formatLocation(
      shipperLane?.deliveryCity,
      shipperLane?.deliveryStateCode,
      shipperLane?.deliveryPostalCode,
      shipperLane?.deliveryPostalCode3
    ),
  })
)
